import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useScopedUrl } from '../nav/scopeOrg';
import type { Device } from '@cornerstone/shared';
import {
  devicePowerBlockReason,
  deviceRebootBlockReason,
  isVerifiedHikvisionPowerDevice,
  formatClock,
  localDateKey,
  nextPowerWindow,
  parseClock,
  type DevicePowerSchedule,
  type HolidayPowerRule,
  type IdleBehavior,
  type PowerScheduleEntry,
} from '@cornerstone/shared';
import { API_BASE } from '../client/config';
import { usePublicHolidays } from '../client/publicHolidays';
import { Button, Panel, StatusBadge } from './ui';
import { ConfirmModal } from './menu';
import { describeApiError, toast } from './toast';
import { cn } from '../utils/cn';

// =============================================================================
// 기기 전원 관리 (SCDS — Hikvision DS-D6055UN-DP)
//
// 패널 펌웨어는 "다음 1회분 off/on" 슬롯 하나만 들고 있고, 그 슬롯은 발동하면
// 비워진다. 그래서 서버가 요일별 계획을 보관하고 Player 가 부팅 직후와 주기적으로
// 다시 밀어 넣는다. 이 화면은 그 계획을 편집하고, 지금 실제로 반영됐는지와
// 화면이 꺼져 있는지를 보여준다.
//
// 화면 구성은 운영자가 실제로 하는 일을 따른다. 역은 대개 매일 같은 시간에 열기
// 때문에 기본은 시간 한 쌍 + 요일 칩이고, 요일마다 다를 때만 펼친다. 맨 위
// 24시간 트랙이 "이 화면이 언제 켜져 있나"에 읽지 않고 답한다.
//
// 기기 자체 설정(Settings › Advanced › Scheduled Startup/Shutdown)은 앱이 멈췄을
// 때를 위한 오프라인 안전망이므로 그대로 두는 것을 전제로 안내한다.
// =============================================================================

const WEEKDAYS = ['일', '월', '화', '수', '목', '금', '토'] as const;
const DEFAULT_ON = '08:00';
const DEFAULT_OFF = '19:00';
const DEFAULT_HOLIDAY_ON = '10:00';
const DEFAULT_HOLIDAY_OFF = '18:00';
const MINUTE_STEP = 5;

type Row = { active: boolean; on: string; off: string };
/** 공휴일 편집값. 'weekly' 는 규칙 없음 — 공휴일도 요일 스케줄대로 동작한다. */
type HolidayDraft = { mode: 'weekly' | 'off' | 'on'; on: string; off: string };

function toHolidayDraft(schedule: DevicePowerSchedule | null): HolidayDraft {
  const rule = schedule?.holiday;
  if (rule?.mode === 'on') return { mode: 'on', on: rule.on, off: rule.off };
  return { mode: rule?.mode === 'off' ? 'off' : 'weekly', on: DEFAULT_HOLIDAY_ON, off: DEFAULT_HOLIDAY_OFF };
}

function toHolidayRule(draft: HolidayDraft): HolidayPowerRule | null {
  if (draft.mode === 'off') return { mode: 'off' };
  if (draft.mode === 'on') return { mode: 'on', on: draft.on, off: draft.off };
  return null;
}

function sameHoliday(a: HolidayDraft, b: HolidayDraft): boolean {
  return a.mode === b.mode && (a.mode !== 'on' || (a.on === b.on && a.off === b.off));
}

function holidayLabel([date, name]: [string, string]): string {
  const [y, m, d] = date.split('-').map(Number);
  const day = new Date(y!, m! - 1, d!).toLocaleDateString('ko-KR', { month: 'numeric', day: 'numeric', weekday: 'short' });
  return `${day} ${name}`;
}

function toRows(schedule: DevicePowerSchedule | null): Row[] {
  const byWeekday = new Map((schedule?.entries ?? []).map(e => [e.weekday, e]));
  return WEEKDAYS.map((_, weekday) => {
    const entry = byWeekday.get(weekday);
    return entry
      ? { active: true, on: entry.on, off: entry.off }
      : { active: false, on: DEFAULT_ON, off: DEFAULT_OFF };
  });
}

function toSchedule(rows: Row[], enabled: boolean, holiday: HolidayDraft): DevicePowerSchedule {
  const entries: PowerScheduleEntry[] = [];
  rows.forEach((row, weekday) => {
    if (row.active) entries.push({ weekday, on: row.on, off: row.off });
  });
  const rule = toHolidayRule(holiday);
  return rule ? { enabled, entries, holiday: rule } : { enabled, entries };
}

function sameRows(a: Row[], b: Row[]): boolean {
  return a.every((row, i) => row.active === b[i]!.active && row.on === b[i]!.on && row.off === b[i]!.off);
}

/** 운영일의 시간이 모두 같으면 "매일 같은 시간"으로 편집한다. */
function isUniform(rows: Row[]): boolean {
  const open = rows.filter(row => row.active);
  return open.length <= 1 || open.every(row => row.on === open[0]!.on && row.off === open[0]!.off);
}

/**
 * 24시간 트랙 위의 점등 구간. 꺼짐이 켜짐보다 이르면 자정을 넘기는 심야 운영이라
 * 두 조각으로 나뉜다.
 */
function segments(on: string, off: string): Array<{ left: number; width: number }> {
  const start = parseClock(on);
  const end = parseClock(off);
  if (start === null || end === null) return [];
  const pct = (m: number) => (m / 1440) * 100;
  if (end > start) return [{ left: pct(start), width: pct(end - start) }];
  return [
    { left: 0, width: pct(end) },
    { left: pct(start), width: pct(1440 - start) },
  ];
}

function isOvernight(row: Row): boolean {
  const on = parseClock(row.on);
  const off = parseClock(row.off);
  return on !== null && off !== null && off <= on;
}

const SELECT_CLASS =
  'rounded border border-line bg-panel-2 px-2.5 py-2 text-[16px] text-text tabular-nums '
  + 'disabled:cursor-not-allowed disabled:opacity-40';

const COMPACT_SELECT_CLASS =
  'rounded border border-line bg-panel-2 px-1.5 py-1 text-[13px] text-text tabular-nums '
  + 'disabled:cursor-not-allowed disabled:opacity-40';

/** 저장된 값이 5분 배수가 아니어도 그 값을 고를 수 있게 목록에 넣어 준다. */
function minuteOptions(current: number): number[] {
  const steps = Array.from({ length: 60 / MINUTE_STEP }, (_, i) => i * MINUTE_STEP);
  return steps.includes(current) ? steps : [...steps, current].sort((a, b) => a - b);
}

/**
 * 시·분 선택. 네이티브 time 입력은 로케일에 따라 오전/오후와 시계 팝오버가 붙어
 * 조작이 길어지는데, 사이니지 운영시간은 5분 단위면 충분하다.
 */
function TimeSelect({
  label,
  value,
  onChange,
  disabled,
  compact = false,
}: {
  label: string;
  value: string;
  onChange: (next: string) => void;
  disabled: boolean;
  /** 요일 행에 그대로 얹는 형태 — 라벨을 떼고 높이를 줄여 줄 수를 늘리지 않는다. */
  compact?: boolean;
}) {
  const minutes = parseClock(value) ?? 0;
  const hour = Math.floor(minutes / 60);
  const minute = minutes % 60;
  const box = compact ? COMPACT_SELECT_CLASS : SELECT_CLASS;
  const selects = (
    <div className="flex items-center gap-1">
      <select
        aria-label={`${label} 시`}
        className={box}
        value={hour}
        disabled={disabled}
        onChange={event => onChange(formatClock(Number(event.target.value) * 60 + minute))}
      >
        {Array.from({ length: 24 }, (_, h) => (
          <option key={h} value={h}>{String(h).padStart(2, '0')}</option>
        ))}
      </select>
      <span className="text-text-dimmer">:</span>
      <select
        aria-label={`${label} 분`}
        className={box}
        value={minute}
        disabled={disabled}
        onChange={event => onChange(formatClock(hour * 60 + Number(event.target.value)))}
      >
        {minuteOptions(minute).map(m => (
          <option key={m} value={m}>{String(m).padStart(2, '0')}</option>
        ))}
      </select>
    </div>
  );
  if (compact) return selects;
  return (
    <div className="flex flex-col gap-1.5">
      <span className="text-[12px] text-text-dimmer">{label}</span>
      {selects}
    </div>
  );
}

/** 트랙 위 x 좌표를 5분 단위 분값으로. 0~1440(자정) 까지 허용한다. */
function minutesAtX(clientX: number, rect: DOMRect): number {
  const ratio = (clientX - rect.left) / rect.width;
  const raw = Math.round((ratio * 1440) / MINUTE_STEP) * MINUTE_STEP;
  return Math.min(1440, Math.max(0, raw));
}

type DragKind = 'on' | 'off' | 'move';

/**
 * 요일별 운영시간을 트랙에서 직접 잡아 끈다. 숫자를 두 번 고르는 것보다 하루의
 * 모양을 보면서 맞추는 편이 빠르고, 요일끼리 비교도 눈으로 된다.
 *
 * 드래그만 되는 컨트롤은 키보드 사용자를 버리므로 양 끝을 slider 로 만들어
 * 방향키(5분)와 Shift+방향키(1시간), Home/End 로도 움직인다.
 */
function DayTimeline({
  row,
  disabled,
  onChange,
  label,
}: {
  row: Row;
  disabled: boolean;
  onChange: (patch: { on?: string; off?: string }) => void;
  label: string;
}) {
  const trackRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<{ kind: DragKind; grabbedAt: number; startOn: number } | null>(null);
  const [dragging, setDragging] = useState(false);

  const on = parseClock(row.on) ?? 0;
  const off = parseClock(row.off) ?? 0;
  // 자정을 넘기면 off 가 작아지므로 길이는 감싸서 센다.
  const span = ((off - on) + 1440) % 1440 || 1440;

  const apply = useCallback((kind: DragKind, minutes: number, grabbedAt: number, startOn: number) => {
    if (kind === 'on') {
      if (minutes % 1440 === off % 1440) return; // 길이 0 은 만들지 않는다.
      onChange({ on: formatClock(minutes) });
      return;
    }
    if (kind === 'off') {
      if (minutes % 1440 === on % 1440) return;
      onChange({ off: formatClock(minutes) });
      return;
    }
    const shifted = startOn + (minutes - grabbedAt);
    onChange({ on: formatClock(shifted), off: formatClock(shifted + span) });
  }, [on, off, span, onChange]);

  useEffect(() => {
    if (!dragging) return;
    const move = (event: PointerEvent) => {
      const track = trackRef.current;
      const drag = dragRef.current;
      if (!track || !drag) return;
      apply(drag.kind, minutesAtX(event.clientX, track.getBoundingClientRect()), drag.grabbedAt, drag.startOn);
    };
    const end = () => { dragRef.current = null; setDragging(false); };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', end);
    window.addEventListener('pointercancel', end);
    return () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', end);
      window.removeEventListener('pointercancel', end);
    };
  }, [dragging, apply]);

  function startDrag(kind: DragKind, event: React.PointerEvent) {
    if (disabled) return;
    event.preventDefault();
    const track = trackRef.current;
    if (!track) return;
    dragRef.current = {
      kind,
      grabbedAt: minutesAtX(event.clientX, track.getBoundingClientRect()),
      startOn: on,
    };
    setDragging(true);
  }

  function nudge(kind: 'on' | 'off', event: React.KeyboardEvent) {
    if (disabled) return;
    const step = event.shiftKey ? 60 : MINUTE_STEP;
    const current = kind === 'on' ? on : off;
    let next: number | null = null;
    if (event.key === 'ArrowLeft' || event.key === 'ArrowDown') next = current - step;
    if (event.key === 'ArrowRight' || event.key === 'ArrowUp') next = current + step;
    if (event.key === 'Home') next = 0;
    if (event.key === 'End') next = 1440 - MINUTE_STEP;
    if (next === null) return;
    event.preventDefault();
    onChange({ [kind]: formatClock(((next % 1440) + 1440) % 1440 || (next === 1440 ? 1440 : 0)) });
  }

  const pct = (m: number) => (m / 1440) * 100;
  const fills = segments(row.on, row.off);

  return (
    <div
      ref={trackRef}
      className={cn(
        // 모바일은 끌기 대신 옆의 시각 선택만 쓴다(2026-09-23 합의) — 좁은 폭에서 트랙이 0으로 찌그러진다.
        'relative h-7 flex-1 overflow-hidden rounded-sm bg-panel-3 max-md:hidden',
        dragging && 'cursor-grabbing',
      )}
    >
      {fills.map(seg => (
        <div
          key={seg.left}
          onPointerDown={event => startDrag('move', event)}
          className={cn('absolute inset-y-0 bg-accent/70', !disabled && 'cursor-grab')}
          style={{ left: `${seg.left}%`, width: `${seg.width}%` }}
        />
      ))}

      {(['on', 'off'] as const).map(kind => (
        <div
          key={kind}
          role="slider"
          tabIndex={disabled ? -1 : 0}
          aria-label={`${label} ${kind === 'on' ? '켜짐' : '꺼짐'} 시각`}
          aria-valuemin={0}
          aria-valuemax={1440}
          aria-valuenow={kind === 'on' ? on : off}
          aria-valuetext={kind === 'on' ? row.on : row.off}
          aria-disabled={disabled}
          onPointerDown={event => startDrag(kind, event)}
          onKeyDown={event => nudge(kind, event)}
          className={cn(
            'absolute inset-y-0 -ml-1.5 w-3 rounded-sm border border-accent bg-panel',
            'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-accent-ring',
            !disabled && 'cursor-ew-resize',
          )}
          style={{ left: `${pct(kind === 'on' ? on : off)}%` }}
        />
      ))}
    </div>
  );
}

function Segmented<T extends string>({
  value,
  options,
  onChange,
  disabled,
  label,
}: {
  value: T;
  options: ReadonlyArray<{ value: T; label: string }>;
  onChange: (value: T) => void;
  disabled: boolean;
  label: string;
}) {
  return (
    <div role="group" aria-label={label} className="inline-flex rounded border border-line bg-panel-2 p-0.5">
      {options.map(option => (
        <button
          key={option.value}
          type="button"
          aria-pressed={value === option.value}
          disabled={disabled}
          onClick={() => onChange(option.value)}
          className={cn(
            'rounded-sm px-3 py-1.5 text-[13px] transition-colors duration-fast disabled:cursor-not-allowed disabled:opacity-40',
            value === option.value ? 'bg-panel-4 text-text' : 'text-text-dim hover:text-text',
          )}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

export function DevicePowerCard({
  device,
  className,
  readOnly = false,
  onChanged,
}: {
  device: Device;
  className?: string;
  readOnly?: boolean;
  onChanged?: () => void;
}) {
  const scoped = useScopedUrl();
  const [enabled, setEnabled] = useState(device.powerSchedule?.enabled ?? false);
  const [rows, setRows] = useState<Row[]>(() => toRows(device.powerSchedule));
  const [holiday, setHoliday] = useState<HolidayDraft>(() => toHolidayDraft(device.powerSchedule));
  const [idleBehavior, setIdleBehavior] = useState<IdleBehavior>(device.idleBehavior);
  const [saving, setSaving] = useState(false);
  const [confirmPowerOff, setConfirmPowerOff] = useState(false);
  const [confirmReboot, setConfirmReboot] = useState(false);
  const [rebootSending, setRebootSending] = useState(false);
  // 슬라이더는 조작 전에는 기기가 보고한 값을 따라가고, 조작한 뒤에는 그 값을
  // 유지한다. 잠금은 기기가 실제로 바뀐 밝기를 보고했을 때만 푼다. 하트비트는
  // 명령 실행보다 먼저 찍힐 수 있어서 '다음 보고'만으로 풀면 옛 값이 그대로
  // 보인다. 요청값과 정확히 일치할 때만 푸는 것도 안 되는데, 패널이 값을
  // 보정해 돌려주면 영영 안 풀리기 때문이다.
  const reportedBacklight = device.nativeStatus?.backlight;
  const [pendingBacklight, setPendingBacklight] = useState<number | null>(null);
  // 반영을 기다리는 동안 이 카드가 직접 읽어온 값. 화면 전체를 다시 그리지
  // 않으려고 전역 갱신 대신 이 항목만 따로 조회한다.
  const [liveBacklight, setLiveBacklight] = useState<number | null>(null);
  const beforeApply = useRef<number | null>(null);
  const [applyingBacklight, setApplyingBacklight] = useState(false);
  const settledBacklight = liveBacklight ?? (typeof reportedBacklight === 'number' ? reportedBacklight : null);
  const backlight = pendingBacklight ?? settledBacklight ?? 60;
  useEffect(() => {
    if (beforeApply.current === null || typeof reportedBacklight !== 'number') return;
    if (reportedBacklight === beforeApply.current) return; // 아직 반영 전의 보고다.
    beforeApply.current = null;
    setPendingBacklight(null);
    setApplyingBacklight(false);
  }, [reportedBacklight]);
  // 기기가 끝내 보고하지 않아도 조작을 영영 잠그지는 않는다.
  useEffect(() => {
    if (!applyingBacklight) return;
    // 이 파일에는 전원 구간을 뜻하는 지역 window 가 있어 전역을 명시한다.
    const timer = globalThis.setTimeout(() => { beforeApply.current = null; setApplyingBacklight(false); }, 120_000);
    return () => globalThis.clearTimeout(timer);
  }, [applyingBacklight]);
  // 반영을 기다리는 동안 이 기기의 상태만 짧게 확인한다. 실제로 값이 바뀐
  // 보고가 와야 잠금을 푼다 — 하트비트는 명령 실행보다 먼저 찍힐 수 있다.
  useEffect(() => {
    if (!applyingBacklight) return;
    let stopped = false;
    const check = async () => {
      try {
        const response = await fetch(scoped(`${API_BASE}/api/devices/${device.id}`));
        if (!response.ok) return;
        const fresh = await response.json() as { nativeStatus?: { backlight?: number } };
        const value = fresh.nativeStatus?.backlight;
        if (stopped || typeof value !== 'number' || value === beforeApply.current) return;
        beforeApply.current = null;
        setLiveBacklight(value);
        setPendingBacklight(null);
        setApplyingBacklight(false);
      } catch { /* 일시적인 실패는 다음 주기에 다시 본다 */ }
    };
    const poll = globalThis.setInterval(() => void check(), 5_000);
    return () => { stopped = true; globalThis.clearInterval(poll); };
  }, [applyingBacklight, device.id]);
  // 바깥에서 새 기기 정보가 들어오면 이 카드가 들고 있던 값은 버린다.
  useEffect(() => { setLiveBacklight(null); }, [reportedBacklight]);
  const rebootBlocked = deviceRebootBlockReason(device);
  useEffect(() => {
    if (devicePowerBlockReason(device)) setConfirmPowerOff(false);
  }, [device.online, device.powerCapable, device.nativeStatus?.sdkAvailable, device.nativeReportedAt]);
  useEffect(() => { setConfirmReboot(false); }, [device.id, device.online, device.powerCapable, device.nativeStatus?.sdkAvailable, device.nativeReportedAt, device.appVersion]);

  // 다른 화면에서 저장했거나 기기가 바뀌면 편집 중이 아닌 값을 따라간다.
  useEffect(() => {
    const next = toRows(device.powerSchedule);
    setEnabled(device.powerSchedule?.enabled ?? false);
    setRows(next);
    setHoliday(toHolidayDraft(device.powerSchedule));
    setIdleBehavior(device.idleBehavior);
  }, [device.id, device.powerSchedule, device.idleBehavior]);

  // 공휴일 규칙을 고른 뒤에만 목록이 필요하다. 미리보기용이라 실패해도 편집은 막지 않는다.
  const { holidays, loaded: holidaysLoaded } = usePublicHolidays(enabled && holiday.mode !== 'weekly');

  const saved = useMemo(() => toRows(device.powerSchedule), [device.powerSchedule]);
  const dirty =
    enabled !== (device.powerSchedule?.enabled ?? false)
    || idleBehavior !== device.idleBehavior
    || !sameRows(rows, saved)
    || !sameHoliday(holiday, toHolidayDraft(device.powerSchedule));

  const invalid = rows.some(row => row.active && (parseClock(row.on) === null || parseClock(row.off) === null))
    || (holiday.mode === 'on' && (parseClock(holiday.on) === null || parseClock(holiday.off) === null));
  const openDays = rows.filter(row => row.active).length;
  const noDays = enabled && openDays === 0;
  const draft = toSchedule(rows, enabled, holiday);

  // 요약과 트랙은 저장 전 편집값을 비춘다 — 저장을 누르기 전에 무엇이 바뀌는지
  // 보이는 편이 낫다. 배지의 "반영됨"만 저장된 사실을 말한다.
  const lead = rows.find(row => row.active) ?? rows[0]!;
  const headline = !enabled
    ? '기기 자체 설정으로 동작'
    : openDays === 0
      ? '운영 요일 없음'
      : !isUniform(rows)
        ? '요일마다 다름'
        : `${lead.on} – ${lead.off}`;
  const weekDays = !enabled
    ? ''
    : openDays === 7
      ? '매일'
      : openDays === 0
        ? '기기가 켜지지 않습니다'
        : WEEKDAYS.filter((_, i) => rows[i]!.active).join('');
  const holidaySummary = !enabled || openDays === 0 ? ''
    : holiday.mode === 'off' ? '공휴일 끔'
      : holiday.mode === 'on' ? `공휴일 ${holiday.on} – ${holiday.off}` : '';
  const headlineDays = [weekDays, holidaySummary].filter(Boolean).join(' · ');

  const window = enabled && !invalid && !noDays ? nextPowerWindow(draft, Date.now(), undefined, undefined, holidays.keys()) : null;
  const today = localDateKey(new Date());
  const soon = [...holidays].filter(([date]) => date >= today).sort(([a], [b]) => a.localeCompare(b)).slice(0, 4);
  const fmt = (ms: number) =>
    new Date(ms).toLocaleString('ko-KR', { month: 'numeric', day: 'numeric', weekday: 'short', hour: '2-digit', minute: '2-digit' });

  const screenDark = device.screenOffAt !== null;

  function setAllDays(active: boolean) {
    setRows(current => current.map(row => ({ ...row, active })));
  }

  function patchDay(weekday: number, patch: Partial<Row>) {
    setRows(current => current.map((row, i) => (i === weekday ? { ...row, ...patch } : row)));
  }

  /** 같은 시간 모드에서는 운영 중인 모든 요일이 함께 움직인다. */
  /**
   * 시각만 맞춘다. 쉬는 요일을 켜지는 않는다 — 통일은 시간을 맞추는 동작이지
   * 운영 요일을 바꾸는 동작이 아니다. 쉬는 요일의 시각도 함께 맞춰 두어, 나중에
   * 그 요일을 켤 때 혼자 다른 시간으로 살아나지 않게 한다.
   */
  function setUniformTime(patch: { on?: string; off?: string }) {
    setRows(current => current.map(row => ({ ...row, ...patch })));
  }

  async function save() {
    if (invalid || readOnly || devicePowerBlockReason(device)) return;
    setSaving(true);
    try {
      const response = await fetch(scoped(`${API_BASE}/api/devices/${device.id}/power-schedule`), {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ schedule: enabled && !noDays ? draft : null }),
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      if (idleBehavior !== device.idleBehavior) {
        const profile = await fetch(scoped(`${API_BASE}/api/devices/${device.id}/profile`), {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ idleBehavior }),
        });
        if (!profile.ok) throw new Error(`HTTP ${profile.status}`);
      }
      toast('전원 설정을 저장했습니다. 기기 반영까지 최대 15분 걸립니다.', 'success');
      onChanged?.();
    } catch (error) {
      toast(describeApiError(error), 'error');
    } finally {
      setSaving(false);
    }
  }

  async function sendCommand(command: 'screenOff' | 'screenOn' | 'powerOff' | 'reboot' | 'setBacklight', label: string,
    payload?: Record<string, unknown>) {
    if (readOnly || devicePowerBlockReason(device)) return;
    if (command === 'reboot' && (rebootBlocked || rebootSending)) return;
    if (command === 'reboot') setRebootSending(true);
    try {
      const response = await fetch(scoped(`${API_BASE}/api/devices/${device.id}/commands`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload ? { command, payload } : { command }),
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      toast(command === 'setBacklight'
        ? `${label} 명령을 보냈습니다. 기기가 반영하면 값이 갱신됩니다.`
        : `${label} 명령을 보냈습니다. 기기가 다음 확인 주기에 실행합니다.`, 'success');
      // 밝기는 기기가 되읽은 값을 보고할 때까지 기다린다. 여기서 화면을 다시
      // 불러오면 조작 직후에 옛 값으로 튀어 보인다.
      if (command !== 'setBacklight') onChanged?.();
    } catch (error) {
      toast(describeApiError(error), 'error');
      // 전송 자체가 실패했으면 기다릴 이유가 없다.
      if (command === 'setBacklight') { beforeApply.current = null; setApplyingBacklight(false); }
    } finally {
      if (command === 'reboot') setRebootSending(false);
    }
  }

  // 상세 링크·기기 이름으로 들어와도 미확인 기기에 전원 설정을 노출하지 않는다.
  // HIKVISION 으로 분류된 기기는 오프라인이어도 카드를 남기고 사유를 보인다(잠금은 사유 문구로 드러난다).
  const classified = device.runtime?.deviceClass === 'hikvision_apk';
  if (!classified && !isVerifiedHikvisionPowerDevice(device)) return null;
  // 기기 확인이 끊겨도 카드를 감추지 않는다. 감추면 조작자가 상태를 볼 수도,
  // 다시 확인할 수도 없어 화면이 고장난 것처럼 보인다. 사유는 위에 띄우고
  // 조작만 잠근다.
  const powerBlocked = devicePowerBlockReason(device);
  // 사유가 있으면 조작만 잠근다. 화면은 그대로 두어 상태를 읽고 다시 확인할 수 있게 한다.
  const locked = readOnly || !!powerBlocked;

  return (
    <Panel
      className={className ?? ''}
      title="전원 관리"
      trailing={
        <div className="flex items-center gap-2">
          {screenDark && <StatusBadge tone="red">화면 꺼짐</StatusBadge>}
          <StatusBadge tone={device.runtime?.fingerprintLocked ? 'red'
            : powerBlocked ? 'dim'
            : device.powerSchedule ? (device.powerAppliedAt ? 'green' : 'dim') : 'dim'}>
            {device.runtime?.fingerprintLocked ? '잠김'
              : powerBlocked ? (device.online ? '확인 대기' : '오프라인')
              : device.powerSchedule
                ? device.powerAppliedAt
                  ? `기기 반영됨 · ${new Date(device.powerAppliedAt).toLocaleString('ko-KR', { hour: '2-digit', minute: '2-digit' })}`
                  : '기기 반영 대기'
                : '서버 미관리'}
          </StatusBadge>
          <button
            type="button"
            title="기기 상태를 다시 가져옵니다"
            aria-label="상태 새로고침"
            onClick={() => globalThis.dispatchEvent(new Event('bricklit:device-changed'))}
            className="rounded border border-line px-2 py-1 text-[12px] text-text-dim hover:text-text"
          >
            상태 새로고침
          </button>
        </div>
      }
    >
      <div className="flex flex-col gap-5">
        {powerBlocked && (
          <div className="rounded border border-line bg-panel-2 px-3 py-2.5">
            <p className="text-[13px] leading-relaxed text-text-dim">{powerBlocked}</p>
            {device.powerSchedule?.enabled && <p className="mt-1 text-[12px] text-text-dimmer">
              저장된 주간 전원 계획은 기기가 마지막으로 받은 사본으로 계속 동작합니다.
            </p>}
          </div>
        )}

        {/* 1. 먼저 답한다 — 이 화면이 언제 켜져 있는가. 요일별 막대는 아래
            편집기와 같은 내용이라 여기서는 문장으로만 답한다. */}
        <div className="flex flex-col gap-3">
          <div className="flex flex-wrap items-baseline gap-3">
            <span className="text-[22px] font-semibold tracking-tight2 tabular-nums text-text">{headline}</span>
            {headlineDays && <span className="text-[13px] text-text-dimmer">{headlineDays}</span>}
          </div>

          {window && (
            <p className="text-[13px] tabular-nums text-text-dim">
              다음 꺼짐 {fmt(window.offAt)} · 다음 켜짐 {fmt(window.onAt)}
            </p>
          )}
        </div>

        {screenDark && (
          <div className="flex flex-wrap items-center justify-between gap-3 rounded border border-red/40 bg-red/10 px-3 py-2.5">
            <div className="flex flex-col gap-0.5">
              <span className="text-[14px] text-text">원격으로 화면을 꺼둔 상태입니다</span>
              <span className="text-[12px] text-text-dim">고장이 아닙니다 · 리모컨 키를 눌러도 켜집니다</span>
            </div>
            <Button onClick={() => void sendCommand('screenOn', '화면 켜기')} disabled={locked}>
              화면 켜기
            </Button>
          </div>
        )}

        <div className="h-px bg-line" />

        {/* 2. 스케줄 편집 — 기본은 매일 같은 시간. */}
        <div className="flex flex-col gap-3.5">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <label className="inline-flex cursor-pointer items-center gap-2.5 text-[15px] text-text">
              <button
                type="button"
                role="switch"
                aria-checked={enabled}
                aria-label="서버에서 전원 스케줄 관리"
                disabled={locked}
                onClick={() => setEnabled(!enabled)}
                className={cn('relative h-5 w-9 flex-none rounded-full transition-colors',
                  enabled ? 'bg-accent' : 'bg-panel-3',
                  locked && 'cursor-not-allowed opacity-50')}
              >
                <span className={cn('absolute top-0.5 h-4 w-4 rounded-full bg-white transition-all',
                  enabled ? 'left-[18px]' : 'left-0.5')} />
              </button>
              <span>서버에서 전원 스케줄 관리</span>
            </label>

            {enabled && (
              // 요일별 편집이 기본이고, 통일은 한 번 누르는 동작으로 둔다.
              // 맨 위 운영 요일의 시각을 나머지 요일에 그대로 복사한다.
              <Button
                className="py-1.5 text-[13px]"
                disabled={locked || openDays === 0 || isUniform(rows)}
                title="맨 위 운영 요일의 시각으로 나머지 요일의 시각을 맞춥니다. 쉬는 요일은 그대로 둡니다."
                onClick={() => setUniformTime({ on: lead.on, off: lead.off })}
              >
                매일 같은 시간으로 설정
              </Button>
            )}
          </div>

          {!enabled && (
            <p className="text-[13px] leading-relaxed text-text-dim">
              지금은 기기 자체 설정의 주간 스케줄로만 켜지고 꺼집니다. 서버에서 관리하면 여기서 요일과 시간을 바꿀 수 있습니다.
            </p>
          )}

          {enabled && (
            <>
              <div className="flex flex-col gap-1.5">
                <div className="flex items-center justify-between gap-3">
                  <span className="text-[12px] text-text-dimmer">운영 요일</span>
                  <div className="flex items-center gap-1">
                    <Button variant="ghost" className="px-2 py-1 text-[12px]" onClick={() => setAllDays(true)} disabled={locked}>
                      전체
                    </Button>
                    <Button variant="ghost" className="px-2 py-1 text-[12px]" onClick={() => setAllDays(false)} disabled={locked}>
                      해제
                    </Button>
                  </div>
                </div>
                <span className={cn('text-[12px]', noDays ? 'text-red' : 'text-text-dimmer')}>
                  {openDays === 7
                    ? '매일 운영합니다'
                    : noDays
                      ? '요일을 하나 이상 골라야 저장됩니다'
                      : '선택하지 않은 요일은 기기를 켜지 않습니다'}
                </span>
              </div>

              {(
                <div className="flex flex-col gap-2">
                  <div className="flex flex-col divide-y divide-line rounded border border-line">
                    {WEEKDAYS.map((label, weekday) => {
                      const row = rows[weekday]!;
                      return (
                        <div
                          key={label}
                          className={cn('flex items-center gap-3 px-3 py-2 max-md:flex-wrap max-md:gap-x-2 max-md:gap-y-1.5', !row.active && 'opacity-50')}
                        >
                          {/* 요일 켜고 끄기를 해당 줄에 둔다. 위에 따로 칩을 두면
                              같은 요일을 두 곳에서 조작하게 된다. */}
                          <button
                            type="button"
                            role="switch"
                            aria-checked={row.active}
                            aria-label={`${label}요일 운영`}
                            disabled={locked}
                            onClick={() => patchDay(weekday, { active: !row.active })}
                            className={cn('relative h-5 w-9 flex-none rounded-full transition-colors',
                              row.active ? 'bg-accent' : 'bg-panel-3',
                              locked && 'cursor-not-allowed opacity-50')}
                          >
                            <span className={cn('absolute top-0.5 h-4 w-4 rounded-full bg-white transition-all',
                              row.active ? 'left-[18px]' : 'left-0.5')} />
                          </button>
                          <span className="w-5 flex-none text-[14px] font-medium text-text">{label}</span>
                          {row.active ? (
                            <>
                              <DayTimeline
                                row={row}
                                label={label}
                                disabled={locked}
                                onChange={patch => patchDay(weekday, patch)}
                              />
                              <span aria-hidden="true" className="hidden basis-full max-md:block" />
                              <TimeSelect
                                label={`${label} 켜짐`}
                                value={row.on}
                                disabled={locked}
                                compact
                                onChange={on => patchDay(weekday, { on })}
                              />
                              <span className="text-text-faint" aria-hidden="true">→</span>
                              <TimeSelect
                                label={`${label} 꺼짐`}
                                value={row.off}
                                disabled={locked}
                                compact
                                onChange={off => patchDay(weekday, { off })}
                              />
                              <span className="w-7 flex-none text-[11px] text-warning">
                                {isOvernight(row) ? '익일' : ''}
                              </span>
                            </>
                          ) : (
                            <span className="flex-1 text-[13px] text-text-dimmer">휴무</span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  <div className="flex items-center gap-3 pl-8 max-md:hidden">
                    <div className="flex flex-1 justify-between text-[10px] tabular-nums text-text-faint">
                      <span>0시</span><span>6시</span><span>12시</span><span>18시</span><span>24시</span>
                    </div>
                    <span className="w-[230px] flex-none text-right text-[11px] text-text-dimmer">
                      막대를 끌거나 옆의 시각을 직접 고릅니다
                    </span>
                  </div>
                </div>
              )}

              {/* 공휴일 — 대한민국 공휴일(대체·임시공휴일 포함)에 요일 스케줄 대신 쓸 동작. */}
              <div className="flex flex-col gap-2.5 rounded border border-line px-3 py-3">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="flex flex-col gap-0.5">
                    <span className="text-[14px] text-text">공휴일에는</span>
                    <span className="text-[12px] text-text-dimmer">
                      {holiday.mode === 'off'
                        ? '하루 종일 켜지 않습니다 · 전날 밤부터 이어진 운영은 끝까지 유지합니다'
                        : holiday.mode === 'on'
                          ? '요일과 상관없이 아래 시간에만 켭니다'
                          : '요일 스케줄대로 켜고 끕니다'}
                    </span>
                  </div>
                  <Segmented
                    label="공휴일 전원"
                    value={holiday.mode}
                    disabled={locked}
                    onChange={mode => setHoliday(current => ({ ...current, mode }))}
                    options={[
                      { value: 'weekly', label: '요일대로' },
                      { value: 'off', label: '끄기' },
                      { value: 'on', label: '켜기' },
                    ]}
                  />
                </div>
                {holiday.mode === 'on' && (
                  <div className="flex flex-wrap items-end gap-3">
                    <TimeSelect label="공휴일 켜짐" value={holiday.on} disabled={locked}
                      onChange={on => setHoliday(current => ({ ...current, on }))} />
                    <span className="pb-2.5 text-text-faint" aria-hidden="true">→</span>
                    <TimeSelect label="공휴일 꺼짐" value={holiday.off} disabled={locked}
                      onChange={off => setHoliday(current => ({ ...current, off }))} />
                    {isOvernight({ active: true, on: holiday.on, off: holiday.off }) && (
                      <span className="pb-2.5 text-[11px] text-warning">익일</span>
                    )}
                  </div>
                )}
                {holiday.mode !== 'weekly' && holidaysLoaded && (
                  <p className="text-[12px] leading-relaxed text-text-dim">
                    {soon.length > 0
                      ? <>다가오는 공휴일 · {soon.map(holidayLabel).join(', ')}</>
                      : '공휴일 정보를 아직 받지 못했습니다. 받기 전까지는 요일 스케줄대로 동작합니다.'}
                  </p>
                )}
              </div>
            </>
          )}
        </div>

        <div className="h-px bg-line" />

        {/* 3. 편성 공백 처리 — 운영자가 실제로 답하는 질문의 형태로. */}
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex flex-col gap-0.5">
            <span className="text-[14px] text-text">운영시간인데 재생할 편성이 없으면</span>
            <span className="text-[12px] text-text-dimmer">
              {idleBehavior === 'screenOff'
                ? '화면만 끄므로 편성이 생기면 다시 켜집니다'
                : '설정한 대기 화면을 띄웁니다'}
            </span>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Segmented
              label="편성 공백 처리"
              value={idleBehavior}
              disabled={locked}
              onChange={setIdleBehavior}
              options={[
                { value: 'idleScreen', label: '대기 화면' },
                { value: 'screenOff', label: '화면 끄기' },
              ]}
            />

          </div>
        </div>

        {/* 4. 기기 자체 안전망 — 한 문장. */}
        <p className="rounded border border-line bg-panel-2 px-3 py-2.5 text-[12.5px] leading-relaxed text-text-dim">
          기기 자체 설정의 주간 스케줄은 앱이 멈췄을 때를 위한 안전망입니다. 이 설정보다 넉넉한 시간으로 켜두세요.
        </p>

        <div className="h-px bg-line" />

        {/* 5. 즉시 제어는 저장과 분리하고, 완전 종료는 그 안에서 또 분리한다. */}
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-[12px] text-text-dimmer">지금 바로</span>
            <Button className="py-1.5 text-[13px]" onClick={() => void sendCommand('screenOff', '화면 끄기')} disabled={locked}>
              화면 끄기
            </Button>
            <Button className="py-1.5 text-[13px]" onClick={() => void sendCommand('screenOn', '화면 켜기')} disabled={locked}>
              화면 켜기
            </Button>
            <span className="mx-1 h-5 w-px bg-line" aria-hidden="true" />
            <label className="flex items-center gap-2 text-[12px] text-text-dimmer" title="밝기를 올리면 패널 온도도 함께 오릅니다.">
              밝기
              <input type="range" min={0} max={100} step={5} value={backlight} disabled={locked || applyingBacklight}
                onChange={(e) => setPendingBacklight(Number(e.target.value))} className="w-28 accent-accent" aria-label="화면 밝기" />
              <span className="w-8 tabular-nums text-text">{backlight}</span>
              {applyingBacklight && <span className="text-text-dimmer">기기 반영 대기</span>}
            </label>
            <Button className="py-1.5 text-[13px]" disabled={locked || applyingBacklight || backlight === settledBacklight}
              onClick={() => {
                beforeApply.current = settledBacklight ?? -1;
                setApplyingBacklight(true);
                void sendCommand('setBacklight', `밝기 ${backlight}`, { value: backlight });
              }}>
              {applyingBacklight ? '적용 중' : '적용'}
            </Button>
            <span className="mx-1 h-5 w-px bg-line" aria-hidden="true" />
            <Button className="py-1.5 text-[13px]" title={rebootBlocked ?? '기기를 재부팅합니다. 전원 예약은 유지됩니다.'}
              onClick={() => setConfirmReboot(true)} disabled={locked || rebootSending || !!rebootBlocked}>
              재시작
            </Button>
            <Button variant="danger" className="py-1.5 text-[13px]" onClick={() => setConfirmPowerOff(true)} disabled={locked}>
              완전 종료
            </Button>
          </div>

          <div className="flex items-center gap-3">
            <span className="text-[12px] text-text-dimmer">저장 후 자동 반영 · 화면 끄기 10초 유예</span>
            <Button variant="primary" onClick={() => void save()} disabled={locked || saving || !dirty || invalid || noDays}>
              {saving ? '저장 중…' : '저장'}
            </Button>
          </div>
        </div>
      </div>

      {confirmReboot && (
        <ConfirmModal title="기기 재시작" confirmLabel="재시작하기"
          message={'기기를 재부팅합니다. 잠시 화면과 연결이 끊긴 뒤 플레이어가 다시 시작됩니다.\n저장된 전원 스케줄은 유지됩니다. 명령은 2분 이내에만 실행됩니다.'}
          onCancel={() => setConfirmReboot(false)}
          onConfirm={async () => { setConfirmReboot(false); await sendCommand('reboot', '기기 재시작'); }} />
      )}
      {confirmPowerOff && (
        <ConfirmModal
          title="완전 종료"
          message={
            '기기를 완전히 종료합니다.\n'
            + '종료 후에는 원격으로 켤 수 없고, 예약된 켜짐 시각에만 다시 켜집니다.\n'
            + (enabled && !noDays
              ? '현재 전원 스케줄이 있으므로 다음 켜짐 시각에 자동으로 복귀합니다.\n'
              : '지금은 전원 스케줄이 없어 현장에서 직접 켜야 합니다.\n')
            + '계속하시겠습니까?'
          }
          confirmLabel="종료"
          danger
          onCancel={() => setConfirmPowerOff(false)}
          onConfirm={async () => {
            setConfirmPowerOff(false);
            await sendCommand('powerOff', '완전 종료');
          }}
        />
      )}
    </Panel>
  );
}
